import { convertToDescriptionApi } from '@/utils/convertToDescriptionApi';
import { Grade } from './AccountInformation.types';
import { EMAIL_STRUCTURE_ERROR } from '@/config/constants';

export const changeEmailMessages = {
  updatedSuccess: 'Your account email address has been updated.',
  updatedError:
    'Unfortunately, we were unable to update your account email address at this time. Please try again in a few minutes. If this issue persists, contact Peachjar at support@peachjar.com for assistance.',
  invalidEmailError: EMAIL_STRUCTURE_ERROR,
};

export const parentMessages = {
  addChildSuccess: 'Your child has been added to your Peachjar account.',
  addChildError:
    'Unfortunately, we were unable to add your child to your Peachjar account at this time. Please try again in a few minutes. If this issue persists, contact Peachjar at support@peachjar.com for assistance.',
  updateChildSuccess: 'Your child’s school information has been updated.',
  updateChildError:
    'Unfortunately, we were unable to update your child’s school information at this time. Please try again in a few minutes. If this issue persists, contact Peachjar at support@peachjar.com for assistance.',
  removeChildSuccess: 'Your child has been removed from your Peachjar account.',
  removeChildError:
    'Unfortunately, we were unable to remove your child from your Peachjar account at this time. Please try again in a few minutes. If this issue persists, contact Peachjar at support@peachjar.com for assistance.',
  changeManageByError:
    'Unfortunately, we were unable to switch your account to be managed by you at this time. Please try again in a few minutes. If this issue persists, contact Peachjar at support@peachjar.com for assistance.',
};

export const bannersMessages = {
  manageByParent:
    'Your family information is currently managed by you. Any updates your district makes will not be reflected in your account.',
  manageByDistrict:
    'Your account information below is currently <b>managed by your district</b>. Your district will make updates to your account as your contact and school information changes over time. To update your child’s school information yourself, select Manage Myself.',
  manageByDistrictDisclaimer:
    'If you choose to manage yourself, your district will no longer be able to make updates. However, we will alert you to any updates your district attempts to make to your child’s school information.',
  emailUpdated:
    'Your Peachjar account email address has been updated. Contact the school district if you would like to update your email address in their system too.',
  childrenEmptyState:
    'Add a child to access flyers from your child’s school and school-approved community enrichment resources.',
  childNotbeUpToDate:
    'Based on new information from your district, your child’s school information might not be up to date.',
  childNotbeUptoDateDisclaimer:
    'If your child is attending the school above, please update your child’s school information below. Otherwise, you can simply dismiss this alert.',
  revertManageBy: `To switch back to your account being managed by your district, select 'Revert to district managed.' This option is available until you make a change to your account email address and/or your child’s school information`,
  emailNeedsToBeVerified:
    'Please check your email for a confirmation email. Click the link to verify your email.',
  multiDistrict:
    '<b>Notice:</b> It appears you have children in multiple districts. Contact our support if you would like to switch the district that is available to you.',
};

export enum GradeLevels {
  ECE = 'ECE',
  PK = 'PK',
  TK = 'TK',
  KG = 'KG',
  FIRST = '1',
  SECOND = '2',
  THIRD = '3',
  FOURTH = '4',
  FIFTH = '5',
  SIXTH = '6',
  SEVENTH = '7',
  EIGTH = '8',
  NINTH = '9',
  TENTH = '10',
  ELEVENTH = '11',
  TWELFTH = '12',
  CE = 'CE',
  N = 'N',
}

export const SCHOOL_NAME_TOOLTIP =
  'Enter the school your child or children attend. Only schools that are using Peachjar are available for selection.';

export const SCHOOL_FIELD_TYPE = 'school_field';
export const GRADE_FIELD_TYPE = 'grade_field';

export const numberToStringMapping = {
  1: 'One',
  2: 'Two',
  3: 'Three',
  4: 'Four',
  5: 'Five',
  6: 'Six',
  7: 'Seven',
  8: 'Eight',
  9: 'Nine',
  10: 'Ten',
  11: 'Eleven',
  12: 'Twelve',
  13: 'Thirteen',
  14: 'Fourteen',
  15: 'Fifteen',
  16: 'Sixteen',
  17: 'Seventeen',
  18: 'Eighteen',
  19: 'Nineteen',
  20: 'Twenty',
};

export enum AgeLevels {
  '2-3' = '2',
  '3-4' = '3',
  '4-5' = '4',
  '5-6' = '5',
  '6-7' = '6',
  '7-8' = '7',
  '8-9' = '8',
  '9-10' = '9',
  '10-11' = '10',
  '11-12' = '11',
  '12-13' = '12',
  '13-14' = '13',
  '14-15' = '14',
  '15-16' = '15',
  '16-17' = '16',
  '17-18' = '17',
  '18-18+' = '18',
  '18+' = '18+',
}

function getKeysByValue<T extends string | number>(enumObj: Record<string, T>, value: T): string {
  return Object.keys(enumObj).find(key => enumObj[key] === value) || '';
}

const formatAges = (description: string) => ({
  id: description,
  description: getKeysByValue(AgeLevels, description),
});

export const ages: Array<Grade> = [
  formatAges(AgeLevels['2-3']),
  formatAges(AgeLevels['3-4']),
  formatAges(AgeLevels['4-5']),
  formatAges(AgeLevels['5-6']),
  formatAges(AgeLevels['6-7']),
  formatAges(AgeLevels['7-8']),
  formatAges(AgeLevels['8-9']),
  formatAges(AgeLevels['9-10']),
  formatAges(AgeLevels['10-11']),
  formatAges(AgeLevels['11-12']),
  formatAges(AgeLevels['12-13']),
  formatAges(AgeLevels['13-14']),
  formatAges(AgeLevels['14-15']),
  formatAges(AgeLevels['15-16']),
  formatAges(AgeLevels['16-17']),
  formatAges(AgeLevels['17-18']),
  formatAges(AgeLevels['18-18+']),
  formatAges(AgeLevels['18+']),
];

export enum GradeLevelsString {
  ECE = 'Early Childhood Education',
  PK = 'Pre-Kindergarten',
  TK = 'Transitional Kindergarten',
  KG = 'Kindergarten',
  FIRST = '1st',
  SECOND = '2nd',
  THIRD = '3rd',
  FOURTH = '4th',
  FIFTH = '5th',
  SIXTH = '6th',
  SEVENTH = '7th',
  EIGTH = '8th',
  NINTH = '9th',
  TENTH = '10th',
  ELEVENTH = '11th',
  TWELFTH = '12th',
  CE = 'Continuing Education Unit',
  N = 'N',
}

const formatGrades = (description: string) => ({
  id: description,
  description: GradeLevelsString[getKeysByValue(GradeLevels, description)],
});

export const gradesString: Array<Grade> = [
  formatGrades(GradeLevels.ECE),
  formatGrades(GradeLevels.PK),
  formatGrades(GradeLevels.TK),
  formatGrades(GradeLevels.KG),
  formatGrades(GradeLevels.FIRST),
  formatGrades(GradeLevels.SECOND),
  formatGrades(GradeLevels.THIRD),
  formatGrades(GradeLevels.FOURTH),
  formatGrades(GradeLevels.FIFTH),
  formatGrades(GradeLevels.SIXTH),
  formatGrades(GradeLevels.SEVENTH),
  formatGrades(GradeLevels.EIGTH),
  formatGrades(GradeLevels.NINTH),
  formatGrades(GradeLevels.TENTH),
  formatGrades(GradeLevels.ELEVENTH),
  formatGrades(GradeLevels.TWELFTH),
  formatGrades(GradeLevels.CE),
];

export const grades: Array<Grade> = [
  convertToDescriptionApi(GradeLevels.ECE),
  convertToDescriptionApi(GradeLevels.PK),
  convertToDescriptionApi(GradeLevels.TK),
  convertToDescriptionApi(GradeLevels.KG),
  convertToDescriptionApi(GradeLevels.FIRST),
  convertToDescriptionApi(GradeLevels.SECOND),
  convertToDescriptionApi(GradeLevels.THIRD),
  convertToDescriptionApi(GradeLevels.FOURTH),
  convertToDescriptionApi(GradeLevels.FIFTH),
  convertToDescriptionApi(GradeLevels.SIXTH),
  convertToDescriptionApi(GradeLevels.SEVENTH),
  convertToDescriptionApi(GradeLevels.EIGTH),
  convertToDescriptionApi(GradeLevels.NINTH),
  convertToDescriptionApi(GradeLevels.TENTH),
  convertToDescriptionApi(GradeLevels.ELEVENTH),
  convertToDescriptionApi(GradeLevels.TWELFTH),
  convertToDescriptionApi(GradeLevels.CE),
];
